import { useCallback, useRef, useState } from "react";

import { Button, FileUpload, Icon } from "@ab-inbev-maz/compass";
import { useAlertStore } from "@/stores/alertStore";
import { isValidFileType } from "@/utils/file";

interface FileUploaderProps {
    title: string;
    description?: string;
    acceptedFileTypes?: string;
    maxSizeMB?: number;
    buttonText?: string;
}

interface FileImagePreviewProps {
    onClear: () => void;
    onUpload: (file: File) => void;
    fileConfiguration: FileUploaderProps;
}

export const FileImagePreview = ({ onClear, onUpload, fileConfiguration }: FileImagePreviewProps) => {
    const {
        title = "Carga de imagen",
        description = "Carga el archivo de imagen",
        acceptedFileTypes = ".png, .jpg, .jpeg, .webp",
        maxSizeMB = 1
    } = fileConfiguration;

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const showAlert = useAlertStore(state => state.showAlert);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const handleFileSelect = (file: File | null) => {
        if (!imageRef.current) return;
        if (file) {
            const url = URL.createObjectURL(file);
            imageRef.current.src = url;
            imageRef.current.classList.remove("hidden");
            setSelectedFile(file);
        }
        else {
            imageRef.current.src = "";
            imageRef.current.classList.add("hidden");
            setSelectedFile(null);
            onClear?.();
        }
    }

    const handleChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
        const file = event?.target.files?.[0];
        if (file) {
            if (!isValidFileType(file, acceptedFileTypes)) {
                showAlert({
                    text: 'Tipo de archivo no permitido',
                    message: `Solo se aceptan: ${acceptedFileTypes}`,
                    status: 'error'
                });
                handleFileSelect(null);
                return;
            }
            if (file.size > maxSizeBytes) {
                showAlert({
                    text: 'Archivo demasiado grande',
                    message: `El archivo excede el tamaño máximo de ${maxSizeMB}MB`,
                    status: 'error'
                });
                handleFileSelect(null);
                return;
            }
            handleFileSelect(file);
            return;
        }
        handleFileSelect(null);
    }

    return (
        <div className="pt-3 pb-6 grid gap-4 px-6">
            <div className="flex flex-col lg:flex-row gap-6">
                <div className="lg:flex-[50%] xl:flex-[70%]">
                    <FileUpload
                        img={<img alt="upload file" className="h-10 w-10" src="data:image/svg+xml,%3csvg%20viewBox='0%200%2040%2040'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20fill-rule='evenodd'%20clip-rule='evenodd'%20d='M0.501221%206.66927C0.501221%204.82832%201.9936%203.33594%203.83455%203.33594H15.8346C16.3292%203.33594%2016.7983%203.55564%2017.1149%203.93563L20.7818%208.33594H36.1679C38.0089%208.33594%2039.5012%209.8283%2039.5012%2011.6693V33.3359C39.5012%2035.1769%2038.0089%2036.6693%2036.1679%2036.6693H3.83455C1.99362%2036.6693%200.501221%2035.1769%200.501221%2033.3359V6.66927Z'%20fill='%23007A78'%20/%3e%3cpath%20d='M24%2021L20%2017L16%2021'%20stroke='%239CFFFD'%20stroke-width='3'%20stroke-linecap='round'%20stroke-linejoin='round'%20/%3e%3cpath%20d='M20%2017L20%2031'%20stroke='%239CFFFD'%20stroke-width='3'%20stroke-linecap='round'%20stroke-linejoin='round'%20/%3e%3c/svg%3e" />}
                        label={title}
                        help={<div className="text-sm text-center max-w-[600px] px-3">El sistema solo permite cargar un archivo, <strong>exclusivamente en formato {acceptedFileTypes.toUpperCase()}</strong>. No se aceptan otros formatos. El tamaño máximo por archivo es de {maxSizeMB}MB.</div>}
                        instructions={<><span className="flex h-6 w-6"><Icon name="file/file-upload" /></span><p className="text-sm font-semibold underline">Selecciona o arrastra aquí tu documento</p></>}
                        accept={acceptedFileTypes}
                        maxSize={maxSizeBytes}
                        maxSizeErrorMessage={`El tamaño del archivo excede el límite de ${maxSizeMB}MB`}
                        multiple={false}
                        onChange={handleChange}
                        className="max-w-full! [&>:first-child]:bg-white [&>ul>li>div>img]:max-h-full [&>ul>li>div>img]:mx-auto"
                    >
                        <p className="text-sm text-gray-500 mt-1">{description}</p>
                    </FileUpload>
                </div>
                <div className="lg:flex-[50%] xl:flex-[30%] flex lg:block xl:flex justify-center">
                    <div className="aspect-[1/1] overflow-hidden w-full max-w-[500px]">
                        <div className="p-12 bg-white w-full h-full rounded-xl border border-gray-200">
                            <div className="w-full h-full overflow-hidden rounded-xl bg-repeat" style={{ backgroundImage: "url('/images/preview.png')" }}>
                                <img alt="" className="object-contain w-full h-full hidden" ref={imageRef} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <Button
                    label="Continuar"
                    variant="primary"
                    className="w-full max-w-xs"
                    disabled={!selectedFile}
                    onClick={() => onUpload(selectedFile!)}
                />
            </div>
        </div>
    )
}